import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Image,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import { Helmet } from 'react-helmet-async';
import { ChevronRightIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import FeatureCard from "../../components/ProductDetails/ProductFeatureCard";
import ServiceCard from "../../components/Common/ServiceCard";
import MobileCoCreationSection from "../../components/Common/MobileCoCreationSection";

const SmartDeliveryPlatformPage = () => {
  const navigate = useNavigate();

  // Key Features Data
  const keyFeatures = [
    {
      icon: "/svg/smart_1.svg",
      title: "Mobile Ordering App",
      description:
        "User-friendly Android/iOS app for browsing, ordering, and live tracking",
    },
    {
      icon: "/svg/smart_2.svg",
      title: "Product & Inventory Management",
      description: "Real-time updates on product availability",
    },
    {
      icon: "/svg/smart_3.svg",
      title: "Delivery Partner Dashboard",
      description: "Assignments, routing, and live status tracking",
    },
    {
      icon: "/svg/product_notification.svg",
      title: "Order Notifications",
      description: "Push, SMS, and email alerts for every order milestone",
    },
    {
      icon: "/svg/smart_5.svg",
      title: "Secure Payment Integration",
      description: "Support for mobile wallets, cards, and cash on-delivery",
    },
    {
      icon: "/svg/product_hand_setting.svg",
      title: "Multi-Vendor & Multi-Warehouse",
      description: "Manage multiple sellers and storage locations seamlessly",
    },
    {
      icon: "/svg/smart_7.svg",
      title: "Promotions & Loyalty Programs",
      description: "Coupons, discounts, and referral incentives",
    },
    {
      icon: "/svg/smart_8.svg",
      title: "Bilingual Support",
      description: "Bangla and English interfaces for wider accessibility",
    },
  ];

  // Add-ons and Integration Data

  // Benefits Data

  // AI & Analytics Integration Data
  const aiAnalyticsFeatures = [
    {
      id: 1,
      title: "Smart Recommendations",
      description: "AI-powered suggestions for purchasing user experience",
      icon: "/svg/smart.svg",
      bgColor: "#E3F2FD",
      iconColor: "#000B8C",
    },
    {
      id: 2,
      title: "Operational Dashboards",
      description:
        "Visualize delivery routes, KPIs, analyst, and KPIs through interactive dashboards",
      icon: "/svg/product_graph.svg",
      bgColor: "#E8F5E8",
      iconColor: "#2E7D32",
    },
  ];

  // Applicable Sectors Data
  const applicableSectors = [
    {
      id: 1,
      title: "Grocery and Food Delivery",
      icon: "/svg/sm_food.svg",
    },
    {
      id: 2,
      title: "Electronics and Consumer Goods",
      icon: "/svg/sm_group.svg",
    },
    {
      id: 3,
      title: "Medical Supplies and Pharmaceuticals",
      icon: "/svg/sm_medical.svg",
    },
    {
      id: 4,
      title: "Office and Industrial Supplies",
      icon: "/svg/sm_supply.svg",
    },
  ];

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>Smart Delivery Platform | Scalable eCommerce & Logistics Solution for Bangladesh</title>
        <meta 
          name="description" 
          content="Explore Business Automation's delivery platform offering real-time order tracking, payment integration, AI analytics, and multi-vendor support. Ideal for diverse industries across Bangladesh." 
        />
        <meta 
          name="keywords" 
          content="Delivery management platform Bangladesh, eCommerce delivery software, AI-powered logistics Bangladesh, order tracking system, multi-vendor delivery solution, mobile ordering app Bangladesh, payment gateway integration Bangladesh, last-mile delivery software, courier integration Bangladesh, digital delivery platform Bangladesh, promotional loyalty software, multilingual delivery system" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/products/smart-delivery-platform" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/products/smart-delivery-platform" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Smart Delivery Platform | Scalable eCommerce & Logistics Solution for Bangladesh" />
        <meta property="og:description" content="Explore Business Automation's delivery platform offering real-time order tracking, payment integration, AI analytics, and multi-vendor support. Ideal for diverse industries across Bangladesh." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/products/smart-delivery-platform" />
        <meta name="twitter:title" content="Smart Delivery Platform | Scalable eCommerce & Logistics Solution for Bangladesh" />
        <meta name="twitter:description" content="Explore Business Automation's delivery platform offering real-time order tracking, payment integration, AI analytics, and multi-vendor support. Ideal for diverse industries across Bangladesh." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <Box
        py={6}
        sx={{
          backgroundImage: "url('/png/background.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "100vh",
        }}
      >
      {/* Breadcrumb Section */}
      <Box>
        <Container maxW="8xl" px={{ base: 4, md: 6 }}>
          <Breadcrumb
            spacing="8px"
            separator={<ChevronRightIcon color="gray.500" />}
            fontSize="sm"
          >
            <BreadcrumbItem>
              <BreadcrumbLink
                color="#000B8C"
                _hover={{ textDecoration: "underline" }}
                onClick={() => navigate("/")}
                cursor="pointer"
              >
                Home
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <Text color="gray.600">Smart Delivery Platform</Text>
            </BreadcrumbItem>
          </Breadcrumb>
        </Container>
      </Box>

      <Container maxW="7xl" py={{ base: 4, md: 6 }}>
        <HStack spacing={{ base: 4, md: 8 }} align="start" wrap="wrap">
          <VStack
            align="start"
            spacing={0}
            flex={{ base: 1, lg: 3 }}
            minW={{ base: "100%", md: "500px", lg: "700px" }}
          >
            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              color="#000B8C"
              lineHeight="1.2"
            >
              Smart Delivery Platform{" "}
              <Text as="span" color="gray.700">
                (eCommerce Platform)
              </Text>
            </Heading>

            {/* Subtitle */}
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              color="gray.600"
              mt={2}
              fontWeight="medium"
              fontStyle={"italic"}
            >
              Seamless Ordering. Trusted Delivery. Scalable
            </Text>

            {/* Description Text */}
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.700"
              lineHeight="1.2"
              mt={4}
              textAlign="justify"
              maxW={{ base: "100%", md: "700px", lg: "800px" }}
            >
              Business Automation Limited presents a powerful Delivery Management
              Platform designed to empower businesses with complete control over
              the order-to-delivery lifecycle. Whether you deliver groceries,
              electronics, pharmaceuticals, or retail goods, our platform
              provides real-time tracking, secure payments, and smooth customer
              experiences—ensuring reliability from order placement to final
              delivery.
            </Text>
          </VStack>

          {/* Right Illustration */}
          <Box
            flex={{ base: 1, lg: 1 }}
            minW={{ base: "100%", md: "250px" }}
            maxW={{ base: "100%", md: "300px" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            h="100%"
          >
            <Image
              src="/png/smart_deli_obj.png"
              alt="Smart Delivery Platform Illustration"
              maxW="100%"
              height="auto"
              borderRadius="lg"
              loading="lazy"
              fallbackSrc="/png/default-product.png"
            />
          </Box>
        </HStack>
      </Container>

      {/* Why Choose Our Delivery Platform Section */}
      <Container maxW="7xl" py={{ base: 8, md: 12 }}>
        <VStack spacing={8} align="center">
          <Heading
            as="h1"
            fontSize={{ base: "2xl", md: "4xl" }}
            fontWeight="bold"
            color="#000B8C"
            lineHeight="1.2"
          >
            Why Choose Our{" "}
            <Text as="span" color="gray.700">
              Delivery Platform
            </Text>
          </Heading>

          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            w="100%"
            py={8}
          >
            <Image
              src="/png/smart_deli_info.png"
              alt="Delivery Platform Features"
              maxW={{ base: "90%", md: "900px" }}
              w="100%"
              borderRadius="lg"
              loading="lazy"
              fallbackSrc="/png/default-features.png"
            />
          </Box>
        </VStack>
      </Container>

      {/* Key Features Section */}
      <Container maxW="7xl" py={{ base: 8, md: 12 }}>
        <VStack spacing={8} align="center">
          <Heading
            as="h1"
            fontSize={{ base: "2xl", md: "4xl" }}
            fontWeight="bold"
            color="#000B8C"
            lineHeight="1.2"
          >
            Key{" "}
            <Text as="span" color="gray.700">
              Features
            </Text>
          </Heading>

          <Box
            display="flex"
            flexWrap="wrap"
            gap={6}
            w="100%"
            mt={8}
            justifyContent="center"
            alignItems="stretch"
          >
            {keyFeatures.map((feature, index) => (
              <Box
                key={index}
                w="100%"
                maxW="400px"
                minW={{ base: "100%", md: "300px" }}
                flex={{
                  base: "1 1 100%",
                  md: "1 1 calc(50% - 12px)",
                  lg: "1 1 calc(33.333% - 16px)",
                }}
              >
                <FeatureCard
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                />
              </Box>
            ))}
          </Box>
        </VStack>
      </Container>

      {/* AI & Analytics Integration Section */}
      <Container maxW="7xl" py={{ base: 12, md: 16 }}>
        <VStack spacing={8} align="center">
          <Heading
            as="h2"
            fontSize={{ base: "2xl", md: "3xl" }}
            fontWeight="bold"
            color="#000B8C"
            textAlign="center"
          ></Heading>
          <Heading
            as="h1"
            fontSize={{ base: "2xl", md: "4xl" }}
            fontWeight="bold"
            color="#000B8C"
            lineHeight="1.2"
          >
            AI & Analytics{" "}
            <Text as="span" color="gray.700">
              Integration
            </Text>
          </Heading>

          <Box
            display="flex"
            flexWrap="wrap"
            gap={6}
            w="100%"
            mt={2}
            justifyContent="center"
            alignItems="stretch"
          >
            {aiAnalyticsFeatures.map((feature) => (
              <Box
                key={feature.id}
                w="100%"
                maxW="400px"
                minW={{ base: "100%", md: "350px" }}
                flex={{ base: "1 1 100%", md: "1 1 calc(50% - 12px)" }}
              >
                <ServiceCard service={feature} />
              </Box>
            ))}
          </Box>
        </VStack>
      </Container>

      {/* Additional Modules Section */}
      <Box
        py={{ base: 12, md: 16 }}
        backgroundImage="/png/share-your-idea.png"
        color="white"
      >
        <Container maxW="7xl">
          <VStack spacing={8} align="center">
            <Heading
              as="h2"
              fontSize={{ base: "2xl", md: "3xl" }}
              fontWeight="bold"
              textAlign="center"
            >
              Additional Modules
            </Heading>

            <HStack spacing={{ base: 6, md: 12 }} align="start" wrap="wrap">
              <VStack
                align="start"
                spacing={4}
                flex={1}
                minW={{ base: "100%", md: "2xl" }}
              >
                <Text fontSize={{ base: "md", md: "lg" }}>
                  • SMS gateway and messaging integration
                </Text>
                <Text fontSize={{ base: "md", md: "lg" }}>
                  • Chatbots and live support features
                </Text>
                <Text fontSize={{ base: "md", md: "lg" }}>
                  • Loyalty program management
                </Text>
                <Text fontSize={{ base: "md", md: "lg" }}>
                  • APIs for ERP, POS, CRM integration
                </Text>
                <Text fontSize={{ base: "md", md: "lg" }}>
                  • 3rd party courier services integration (e.g. local delivery
                  partners)
                </Text>
              </VStack>

              <Box
                flex={1}
                minW={{ base: "100%", md: "300px" }}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Image
                  src="/png/Layer_1.png"
                  alt="Additional Modules"
                  maxW="100%"
                  height="auto"
                  borderRadius="lg"
                  loading="lazy"
                  fallbackSrc="/png/default-modules.png"
                />
              </Box>
            </HStack>
          </VStack>
        </Container>
      </Box>
      <MobileCoCreationSection
        blueTitle="Applicable"
        blackTitle={"Sectors"}
        subtitle=""
        processes={applicableSectors}
        illustrationImage="/png/objects.png"
        footerText=""
      />
      <VStack spacing={8} align="center" py={{ base: 8, md: 12 }}>
        <Heading
          as="h1"
          fontSize={{ base: "2xl", md: "4xl" }}
          fontWeight="bold"
          color="#000B8C"
          lineHeight="1.2"
        >
          Business{" "}
          <Text as="span" color="gray.700">
            Benefits
          </Text>
        </Heading>
        <Image
          src="/png/business_benefits.png"
          alt="Delivery Platform Features"
          maxW={{ base: "90%", md: "6xl" }}
          w="100%"
          borderRadius="lg"
          loading="lazy"
          fallbackSrc="/png/default-features.png"
        />
      </VStack>
    </Box>
    </>
  );
};

export default SmartDeliveryPlatformPage;
